import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { getActiveNetworkData, onceEvent } from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  createDeferredPromise,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  getBuffer,
  getCore,
  randomString,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Transaction, VersionedTransaction } from '@solana/web3.js';
import bs58 from 'bs58';

import { getSolanaConnection } from '../../getSolanaConnection';
import type { SolanaWalletProvider } from '../../SolanaWalletProvider.types';
import { solanaExecuteCheckoutTransaction } from '../../utils/solanaExecuteCheckoutTransaction';
import { solanaTransferAmount } from '../../utils/solanaTransferAmount';
import { NoPhantomSessionError } from '../errors/NoPhantomSessionError';
import type { PhantomEvents } from '../phantomRedirect.broadcast.types';
import type {
  PhantomPendingRequest,
  PhantomSession,
} from '../PhantomRedirectWalletProvider.types';
import { pendingRequestStorageKeySchema } from '../storage/pendingRequestStorageSchema';
import { phantomSessionStorageKeySchema } from '../storage/phantomSessionStorageSchema';
import { buildPhantomDeepLink } from '../utils/buildPhantomDeepLink';
import { encryptPayload } from '../utils/crypto/encryptPayload';
import { generateNaClKeyPair } from '../utils/crypto/generateNaClKeyPair';
import { getPhantomCluster } from '../utils/getPhantomCluster/getPhantomCluster';
import { getPhantomOriginTab } from '../utils/getPhantomOriginTab';
import { listenForPhantomBroadcast } from '../utils/listenForPhantomBroadcast';

export type CreatePhantomRedirectWalletProviderParams = {
  baseRedirectUrl: string;
  dynamicClient: DynamicClient;
};

/**
 * Extracts the dApp URL from a redirect URL.
 */
const extractDappUrl = (redirectUrl: string): string => {
  const url = new URL(redirectUrl);

  return `${url.protocol}//${url.host}`;
};

/**
 * Creates a Phantom redirect-based Solana wallet provider.
 *
 * This wallet provider uses Phantom's deep link protocol for mobile wallet
 * interactions with end-to-end encryption using NaCl.
 *
 * @param params.dynamicClient - The Dynamic client instance
 * @returns A Phantom redirect wallet provider
 */
export const createPhantomRedirectWalletProvider = ({
  baseRedirectUrl,
  dynamicClient,
}: CreatePhantomRedirectWalletProviderParams): SolanaWalletProvider => {
  const core = getCore(dynamicClient);

  const walletProviderType = WalletProviderEnum.DeepLink;

  const key = formatWalletProviderKey({
    chain: 'SOL',
    displayName: 'Phantom',
    walletProviderType,
  });

  const groupKey = formatWalletProviderGroupKey('Phantom');

  const metadata = {
    displayName: 'Phantom',
    icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDgiIGhlaWdodD0iMTA4IiB2aWV3Qm94PSIwIDAgMTA4IDEwOCIgZmlsbD0ibm9uZSI+CjxyZWN0IHdpZHRoPSIxMDgiIGhlaWdodD0iMTA4IiByeD0iMjYiIGZpbGw9IiNBQjlGRjIiLz4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik00Ni41MjY3IDY5LjkyMjlDNDIuMDA1NCA3Ni44NTA5IDM0LjQyOTIgODUuNjE4MiAyNC4zNDggODUuNjE4MkMxOS41ODI0IDg1LjYxODIgMTUgODMuNjU2MyAxNSA3NS4xMzQyQzE1IDUzLjQzMDUgNDQuNjMyNiAxOS44MzI3IDcyLjEyNjggMTkuODMyN0M4Ny43NjggMTkuODMyNyA5NCAzMC42ODQ2IDk0IDQzLjAwNzlDOTQgNTguODI1OCA4My43MzU1IDc2LjkxMjIgNzMuNTMyMSA3Ni45MTIyQzcwLjI5MzkgNzYuOTEyMiA2OC43MDUzIDc1LjEzNDIgNjguNzA1MyA3Mi4zMTRDNjguNzA1MyA3MS41NzgzIDY4LjgyNzUgNzAuNzgxMiA2OS4wNzE5IDY5LjkyMjlDNjUuNTg5MyA3NS44Njk5IDU4Ljg2ODUgODEuMzg3OCA1Mi41NzU0IDgxLjM4NzhDNDcuOTkzIDgxLjM4NzggNDUuNjcxMyA3OC41MDYzIDQ1LjY3MTMgNzQuNDU5OEM0NS42NzEzIDcyLjk4ODQgNDUuOTc2OCA3MS40NTU2IDQ2LjUyNjcgNjkuOTIyOVpNODMuNjc2MSA0Mi41Nzk0QzgzLjY3NjEgNDYuMTcwNCA4MS41NTc1IDQ3Ljk2NTggNzkuMTg3NSA0Ny45NjU4Qzc2Ljc4MTYgNDcuOTY1OCA3NC42OTg5IDQ2LjE3MDQgNzQuNjk4OSA0Mi41Nzk0Qzc0LjY5ODkgMzguOTg4NSA3Ni43ODE2IDM3LjE5MzEgNzkuMTg3NSAzNy4xOTMxQzgxLjU1NzUgMzcuMTkzMSA4My42NzYxIDM4Ljk4ODUgODMuNjc2MSA0Mi41Nzk0Wk03MC4yMTAzIDQyLjU3OTVDNzAuMjEwMyA0Ni4xNzA0IDY4LjA5MTYgNDcuOTY1OCA2NS43MjE2IDQ3Ljk2NThDNjMuMzE1NyA0Ny45NjU4IDYxLjIzMyA0Ni4xNzA0IDYxLjIzMyA0Mi41Nzk1QzYxLjIzMyAzOC45ODg1IDYzLjMxNTcgMzcuMTkzMSA2NS43MjE2IDM3LjE5MzFDNjguMDkxNiAzNy4xOTMxIDcwLjIxMDMgMzguOTg4NSA3MC4yMTAzIDQyLjU3OTVaIiBmaWxsPSIjRkZGREY4Ii8+Cjwvc3ZnPg==',
  };

  // Helper to get session from storage
  const getSession = async (): Promise<PhantomSession> => {
    const session = await core.storage.getItem(phantomSessionStorageKeySchema);

    if (!session) {
      throw new NoPhantomSessionError();
    }

    return session;
  };

  // Helper to set pending request in storage
  const setPendingRequest = async (request: PhantomPendingRequest) => {
    await core.storage.setItem(pendingRequestStorageKeySchema, request);
  };

  type SetupPhantomBroadcastListenersParams<
    K extends keyof PhantomEvents,
    E extends keyof PhantomEvents
  > = {
    completeEvent: K;
    deferredPromise: { promise: Promise<unknown> };
    errorEvent: E;
    onComplete: (args: Parameters<PhantomEvents[K]>[0]) => void;
    onError: (args: Parameters<PhantomEvents[E]>[0]) => void;
  };

  /**
   * Registers listeners for both delivery paths Phantom uses depending on platform:
   * - In mobile native, we get redirected back to the same context, so onceEvent suffices.
   * - In mobile browsers, we get redirected back to a new tab, so a BroadcastChannel listener
   *   is also needed to receive the completion event from that tab.
   *
   * The broadcast channel is closed once the deferred promise settles.
   */
  const setupPhantomListeners = <
    K extends keyof PhantomEvents,
    E extends keyof PhantomEvents
  >({
    completeEvent,
    deferredPromise,
    errorEvent,
    onComplete,
    onError,
  }: SetupPhantomBroadcastListenersParams<K, E>): void => {
    // Mark this client context as the origin so completePhantomRedirect can
    // distinguish it from the clone tab opened by Phantom in a mobile browser.
    getPhantomOriginTab(dynamicClient).isOriginTab = true;

    onceEvent({ event: completeEvent, listener: onComplete }, dynamicClient);
    onceEvent({ event: errorEvent, listener: onError }, dynamicClient);

    const closeChannel = listenForPhantomBroadcast(
      {
        [completeEvent]: onComplete,
        [errorEvent]: onError,
      },
      dynamicClient
    );

    void deferredPromise.promise
      .finally(() => {
        core.logger.debug(
          '[PHANTOM] setupPhantomListeners: deferred promise settled, closing channel'
        );
        closeChannel();
      })
      .catch(() => {
        // Rejection already propagates via deferredPromise.promise — suppress here
      });
  };

  const connect: SolanaWalletProvider['connect'] = async () => {
    const redirectUrl = baseRedirectUrl;

    const { publicKey: dappPublicKey, secretKey: dappSecretKey } =
      generateNaClKeyPair();
    const requestId = randomString({ length: 32 });

    await setPendingRequest({
      dappPublicKey,
      dappSecretKey,
      method: 'connect',
      requestId,
      timestamp: Date.now(),
    });

    const deferredPromise = createDeferredPromise<{
      addresses: Array<{ address: string; publicKey: string }>;
    }>();

    setupPhantomListeners({
      completeEvent: 'phantomRedirectConnectionComplete',
      deferredPromise,
      errorEvent: 'phantomRedirectConnectionError',
      onComplete: ({ address, publicKey }) => {
        core.logger.debug('[PHANTOM] connect: onComplete called', { address });
        deferredPromise.resolve({ addresses: [{ address, publicKey }] });
      },
      onError: ({ error }) => {
        core.logger.debug('[PHANTOM] connect: onError called', { error });
        deferredPromise.reject(error);
      },
    });

    const cluster = await getPhantomCluster({ dynamicClient });

    const deepLinkUrl = buildPhantomDeepLink({
      appUrl: extractDappUrl(redirectUrl),
      cluster,
      dappPublicKey,
      method: 'connect',
      redirectUrl,
    });

    core.logger.debug('[PHANTOM] connect: opening deeplink', { redirectUrl });

    await core.openDeeplink(deepLinkUrl.toString());

    return deferredPromise.promise;
  };

  const disconnect: SolanaWalletProvider['disconnect'] = async () => {
    const session = await getSession();
    const redirectUrl = baseRedirectUrl;

    const { data, nonce } = encryptPayload({
      payload: { session: session.sessionToken },
      sharedSecret: session.sharedSecret,
    });

    const requestId = randomString({ length: 32 });

    await setPendingRequest({
      dappPublicKey: session.dappPublicKey,
      dappSecretKey: session.dappSecretKey,
      method: 'disconnect',
      requestId,
      timestamp: Date.now(),
    });

    const deferredPromise = createDeferredPromise<void>();

    setupPhantomListeners({
      completeEvent: 'phantomRedirectDisconnectComplete',
      deferredPromise,
      errorEvent: 'phantomRedirectDisconnectError',
      onComplete: async () => {
        await core.storage.removeItem(phantomSessionStorageKeySchema);
        deferredPromise.resolve();
      },
      onError: ({ error }) => {
        deferredPromise.reject(error);
      },
    });

    const deepLinkUrl = buildPhantomDeepLink({
      appUrl: extractDappUrl(redirectUrl),
      dappPublicKey: session.dappPublicKey,
      method: 'disconnect',
      nonce,
      payload: data,
      redirectUrl,
    });

    await core.openDeeplink(deepLinkUrl.toString());

    return deferredPromise.promise;
  };

  const getActiveNetworkId: SolanaWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: dynamicClient,
        walletProviderKey: key,
      });

  const getConnectedAddresses: SolanaWalletProvider['getConnectedAddresses'] =
    async () => {
      const session = await getSession();

      return {
        addresses: [session.walletAddress],
      };
    };

  const signMessage: SolanaWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(
      walletAccount,
      'Wallet account is required for Phantom redirect'
    );

    const redirectUrl = baseRedirectUrl;
    const session = await getSession();

    const messageBase58 = bs58.encode(
      new Uint8Array(getBuffer().from(message))
    );

    const { data, nonce } = encryptPayload({
      payload: { message: messageBase58, session: session.sessionToken },
      sharedSecret: session.sharedSecret,
    });

    const requestId = randomString({ length: 32 });

    await setPendingRequest({
      dappPublicKey: session.dappPublicKey,
      dappSecretKey: session.dappSecretKey,
      message,
      method: 'signMessage',
      requestId,
      timestamp: Date.now(),
      walletAddress: walletAccount.address,
    });

    const deferredPromise = createDeferredPromise<{ signature: string }>();

    setupPhantomListeners({
      completeEvent: 'phantomRedirectSignMessageComplete',
      deferredPromise,
      errorEvent: 'phantomRedirectSignMessageError',
      onComplete: ({ signature }) => {
        deferredPromise.resolve({ signature });
      },
      onError: ({ error }) => {
        deferredPromise.reject(error);
      },
    });

    const cluster = await getPhantomCluster({ dynamicClient, walletAccount });

    const deepLinkUrl = buildPhantomDeepLink({
      appUrl: extractDappUrl(redirectUrl),
      cluster,
      dappPublicKey: session.dappPublicKey,
      method: 'signMessage',
      nonce,
      payload: data,
      redirectUrl,
    });

    await core.openDeeplink(deepLinkUrl.toString());

    return deferredPromise.promise;
  };

  const signTransaction: SolanaWalletProvider['signTransaction'] = async ({
    transaction,
    walletAccount,
  }) => {
    assertDefined(
      walletAccount,
      'Wallet account is required for Phantom redirect'
    );

    const redirectUrl = baseRedirectUrl;
    const session = await getSession();

    const serialized =
      transaction instanceof VersionedTransaction
        ? transaction.serialize()
        : (transaction as Transaction).serialize({
            requireAllSignatures: false,
            verifySignatures: false,
          });

    const transactionBase58 = bs58.encode(new Uint8Array(serialized));

    const { data, nonce } = encryptPayload({
      payload: {
        session: session.sessionToken,
        transaction: transactionBase58,
      },
      sharedSecret: session.sharedSecret,
    });

    const requestId = randomString({ length: 32 });

    await setPendingRequest({
      dappPublicKey: session.dappPublicKey,
      dappSecretKey: session.dappSecretKey,
      method: 'signTransaction',
      requestId,
      timestamp: Date.now(),
      walletAddress: walletAccount.address,
    });

    const deferredPromise = createDeferredPromise<{
      signedTransaction: typeof transaction;
    }>();

    const deserializeTransaction = ({
      transaction: signedTransactionBase58,
    }: {
      transaction: string;
    }) => {
      const decoded = bs58.decode(signedTransactionBase58);
      const signedTransaction = (
        transaction instanceof VersionedTransaction
          ? VersionedTransaction.deserialize(decoded)
          : Transaction.from(decoded)
      ) as typeof transaction;

      deferredPromise.resolve({ signedTransaction });
    };

    setupPhantomListeners({
      completeEvent: 'phantomRedirectSignTransactionComplete',
      deferredPromise,
      errorEvent: 'phantomRedirectSignTransactionError',
      onComplete: deserializeTransaction,
      onError: ({ error }) => {
        deferredPromise.reject(error);
      },
    });

    const cluster = await getPhantomCluster({ dynamicClient, walletAccount });

    const deepLinkUrl = buildPhantomDeepLink({
      appUrl: extractDappUrl(redirectUrl),
      cluster,
      dappPublicKey: session.dappPublicKey,
      method: 'signTransaction',
      nonce,
      payload: data,
      redirectUrl,
    });

    await core.openDeeplink(deepLinkUrl.toString());

    return deferredPromise.promise;
  };

  const signAllTransactions: SolanaWalletProvider['signAllTransactions'] =
    async ({ transactions, walletAccount }) => {
      assertDefined(
        walletAccount,
        'Wallet account is required for Phantom redirect'
      );

      const redirectUrl = baseRedirectUrl;
      const session = await getSession();

      const serialized = transactions.map((tx) =>
        tx instanceof VersionedTransaction
          ? tx.serialize()
          : (tx as Transaction).serialize({
              requireAllSignatures: false,
              verifySignatures: false,
            })
      );

      const transactionsBase58 = serialized.map((s) =>
        bs58.encode(new Uint8Array(s))
      );

      const { data, nonce } = encryptPayload({
        payload: {
          session: session.sessionToken,
          transactions: transactionsBase58,
        },
        sharedSecret: session.sharedSecret,
      });

      const requestId = randomString({ length: 32 });

      await setPendingRequest({
        dappPublicKey: session.dappPublicKey,
        dappSecretKey: session.dappSecretKey,
        method: 'signAllTransactions',
        requestId,
        timestamp: Date.now(),
        walletAddress: walletAccount.address,
      });

      const deferredPromise = createDeferredPromise<{
        signedTransactions: typeof transactions;
      }>();

      const deserializeTransactions = ({
        transactions: signedTransactionsBase58,
      }: {
        transactions: string[];
      }) => {
        const signedTransactions = signedTransactionsBase58.map(
          (txBase58, i) => {
            const decoded = bs58.decode(txBase58);

            return transactions[i] instanceof VersionedTransaction
              ? VersionedTransaction.deserialize(decoded)
              : Transaction.from(decoded);
          }
        ) as typeof transactions;

        deferredPromise.resolve({ signedTransactions });
      };

      setupPhantomListeners({
        completeEvent: 'phantomRedirectSignAllTransactionsComplete',
        deferredPromise,
        errorEvent: 'phantomRedirectSignAllTransactionsError',
        onComplete: deserializeTransactions,
        onError: ({ error }) => {
          deferredPromise.reject(error);
        },
      });

      const cluster = await getPhantomCluster({ dynamicClient, walletAccount });

      const deepLinkUrl = buildPhantomDeepLink({
        appUrl: extractDappUrl(redirectUrl),
        cluster,
        dappPublicKey: session.dappPublicKey,
        method: 'signAllTransactions',
        nonce,
        payload: data,
        redirectUrl,
      });

      await core.openDeeplink(deepLinkUrl.toString());

      return deferredPromise.promise;
    };

  // Phantom's signAndSendTransaction deeplink is deprecated. We implement it by
  // calling signTransaction (Phantom signs only) and then broadcasting the
  // signed transaction ourselves via the Solana RPC — the same pattern used by
  // the WaaS provider.
  const signAndSendTransaction: SolanaWalletProvider['signAndSendTransaction'] =
    async ({ options, transaction, walletAccount }) => {
      const { signedTransaction } = await signTransaction({
        transaction,
        walletAccount,
      });

      const { networkData } = await getActiveNetworkData(
        { walletAccount },
        dynamicClient
      );

      assertDefined(
        networkData,
        'Network data is required to broadcast transaction'
      );

      const connection = getSolanaConnection({ networkData });

      const rawTransaction = signedTransaction.serialize();

      const signature = await connection.sendRawTransaction(
        rawTransaction,
        options
      );

      return { signature };
    };

  return {
    chain: 'SOL',
    connect,
    disconnect,
    executeCheckoutTransaction: (args) =>
      solanaExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey,
    key,
    metadata,
    signAllTransactions,
    signAndSendTransaction,
    signMessage,
    signTransaction,
    transferAmount: (args) => solanaTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
